import { FormControl, Grid, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { SINGLE_LINE_INPUT_FIELD_MAX_LENGTH } from "../../../../common/helpers/inputLimits";
import { VerticalAlignGrid } from "../../../invoices/styled";
import CommonTextField from "../../../../common/components/functional/commonTextField/CommonTextField";
import { CustomButton } from "../../../../common/components/functional/customButton/CustomButton";
import { ReactComponent as EditIcon } from "../../../../common/icons/editGray.svg";
import { ReactComponent as ViewIcon } from "../../../../common/icons/viewGray.svg";

interface Props {
  label: string;
  labelWidth?: number;
  fieldName: string;
  fieldType?: "text" | "number";
  textFieldMaxLength?: number;
  disabled?: boolean;
  labelColor?: string;
  inputWidth?: number;
  inputType?: string;
  inputSuffix?: string | null;
  inputSpec?: string | null;
  inputError?: boolean;
  helperText?: string | null;
  decimalScale?: number;
  onOpenModal?: (id: string) => void;
  buttonWidth?: number;
  viewButtonText?: string;
  editButtonText?: string;
  showButton?: boolean;
  showViewButton?: boolean;
}

const DEFAULT_LABEL_WIDTH = 3;
const DEFAULT_FIELD_TYPE = "text";
const DEFAULT_INPUT_WIDTH = 5;
const DEFAULT_TEXT_FIELD_MAX_LENGTH = SINGLE_LINE_INPUT_FIELD_MAX_LENGTH;
const DEFAULT_BUTTON_WIDTH = 4;

const Input = ({ ...props }) => (
  <CommonTextField
    {...props}
    inputProps={{
      style: {
        textAlign: "center",
      },
    }}
  />
);

const LabelInputRow = ({
  label,
  labelWidth = DEFAULT_LABEL_WIDTH,
  fieldName,
  fieldType = DEFAULT_FIELD_TYPE,
  textFieldMaxLength = DEFAULT_TEXT_FIELD_MAX_LENGTH,
  disabled,
  labelColor,
  inputWidth = DEFAULT_INPUT_WIDTH,
  inputType,
  inputSuffix,
  inputSpec,
  inputError,
  helperText,
  decimalScale = 2,
  onOpenModal,
  buttonWidth = DEFAULT_BUTTON_WIDTH,
  viewButtonText,
  editButtonText,
  showButton,
  showViewButton,
}: Props) => {
  const { control } = useFormContext();

  return (
    <VerticalAlignGrid container spacing={1}>
      <Grid
        container
        item
        xs={labelWidth}
        justifyContent="flex-end"
        paddingRight="0.5rem"
      >
        <Typography variant="secondary" color={labelColor}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={inputWidth}>
        <FormControl fullWidth size="small">
          <Controller
            name={fieldName}
            control={control}
            render={({ field: { name, value, onChange } }) =>
              fieldType === "text" ? (
                <CommonTextField
                  name={name}
                  type={inputType}
                  inputProps={{ maxLength: textFieldMaxLength }}
                  value={value}
                  onChange={onChange}
                  disabled={disabled}
                  error={inputError}
                  helperText={!!helperText ? helperText : ""}
                />
              ) : (
                <NumericFormat
                  disabled={disabled}
                  value={value}
                  decimalSeparator=","
                  valueIsNumericString
                  onValueChange={(values) => onChange(values.value)}
                  error={inputError}
                  customInput={Input}
                  decimalScale={decimalScale}
                />
              )
            }
          />
        </FormControl>
      </Grid>
      {inputSuffix && (
        <Grid item sx={{ paddingRight: "0.5rem" }}>
          <Typography>{inputSuffix}</Typography>
        </Grid>
      )}
      {inputSpec && (
        <Grid item>
          <Typography variant="secondary">{inputSpec}</Typography>
        </Grid>
      )}
      {showButton && (
        <Grid item xs={buttonWidth}>
          <CustomButton
            variant="text"
            startIcon={showViewButton ? <ViewIcon /> : <EditIcon />}
            onClick={() => onOpenModal && onOpenModal(fieldName)}
            sx={{ borderRadius: "12px" }}
          >
            <Typography color="textSecondary" fontSize="14px">
              {showViewButton ? viewButtonText : editButtonText}
            </Typography>
          </CustomButton>
        </Grid>
      )}
    </VerticalAlignGrid>
  );
};

export default LabelInputRow;
